import { createDefaultInstrumentPatch, normalizeInstrumentPatch } from './patchSchema';

// CookUp-native interpretations of the voice families in 8116P.rar/PATCHLIST.PNG.
// These are authored patches, not decoded Yamaha AFM/AWM operator data or samples.
const source = (voice, bank) => ({ archive: '8116P.rar', voice, bank, conversion: 'CookUp interpretation' });
const envelope = (decay, sustain = 0, release = 0.3, attack = 0.002) => ({ enabled: true, attack, decay, sustain, release });

function makePatch(id, name, category, reference, changes) {
  const base = createDefaultInstrumentPatch();
  return normalizeInstrumentPatch({
    ...base,
    ...changes,
    id: `sy77-inspired-${id}`,
    name,
    category,
    sourceReference: reference,
    tags: ['SY77 Inspired', '8116P', ...(changes.tags || [])],
    macros: { Punch: 0.22, Metal: 0.06, Dirt: 0, Width: 0.3, Motion: 0, Air: 0.1, Body: 0.5, Space: 0.08, ...changes.macros },
    ampEnvelope: { attack: 0.004, decay: 2.2, sustain: 0.08, release: 0.45, velocity: 0.9, ...changes.ampEnvelope },
    filter: { enabled: true, type: 'lowpass', cutoff: 6500, resonance: 0.55, drive: 0, keyTrack: 0.25, ...changes.filter },
    filter2: { ...base.filter2, enabled: false },
    filterEnvelope: { ...base.filterEnvelope, amount: 0, ...changes.filterEnvelope },
    lfo: { ...base.lfo, enabled: false, ...changes.lfo },
    lfo2: { ...base.lfo2, enabled: false, ...changes.lfo2 },
    stepMod: { ...base.stepMod, enabled: false },
    arp: { ...base.arp, enabled: false },
    performance: { ...base.performance, master: 0.65, glide: 0, voices: 16, ...changes.performance },
    effects: {
      distortion: { ...base.effects.distortion, enabled: false, ...changes.effects?.distortion },
      chorus: { ...base.effects.chorus, enabled: false, ...changes.effects?.chorus },
      delay: { ...base.effects.delay, enabled: false, ...changes.effects?.delay },
      reverb: { ...base.effects.reverb, enabled: true, size: 0.35, damping: 0.65, mix: 0.09, ...changes.effects?.reverb },
      compressor: { ...base.effects.compressor, enabled: true, threshold: -18, ratio: 2, ...changes.effects?.compressor }
    },
    layers: base.layers.map((layer, index) => ({
      ...layer,
      enabled: false, level: 0, detune: 0, unison: 1, width: 0,
      ...changes.layers[index]
    }))
  });
}

// Different bell ratios, transient envelopes, body balances and processing give
// each electric piano a distinct response rather than just a different name.
const ELECTRIC_PIANOS = [
  { id: 'night-hawk', name: 'Night Hawk EP', voice: 'EP:NiteHwk', bank: '1A', ratio: 1, index: 1.9, tine: 0.14, tineRatio: 7, decay: 2.4, cutoff: 3800, body: 0.38, chorus: 0.15 },
  { id: 'stage-76', name: 'Stage 76 EP', voice: 'EP|76Stage', bank: '1A', ratio: 1, index: 1.4, tine: 0.1, tineRatio: 4, decay: 1.9, cutoff: 5300, body: 0.46, contact: 0.012 },
  { id: 'classic-tine', name: 'Classic Tine EP', voice: 'EP:Classic', bank: '1A', ratio: 1, index: 2.4, tine: 0.18, tineRatio: 7, decay: 2.1, cutoff: 7500, body: 0.28 },
  { id: 'bell-tine', name: 'Bell Tine EP', voice: 'EP:Beltine', bank: '1A', ratio: 2, index: 2.2, tine: 0.24, tineRatio: 10, decay: 2.8, cutoff: 10000, body: 0.19 },
  { id: 'dream-tine', name: 'Dream Tine EP', voice: 'Dream Tine', bank: '1A', ratio: 1, index: 1.2, tine: 0.13, tineRatio: 5, decay: 3.8, cutoff: 5200, body: 0.32, chorus: 0.28, space: 0.24 },
  { id: 'ballad-90', name: 'Ballad 90 EP', voice: '90s Ballad', bank: '1A', ratio: 1, index: 2.1, tine: 0.18, tineRatio: 7, decay: 3.1, cutoff: 6900, body: 0.3, chorus: 0.22, space: 0.16 },
  { id: 'crystal-rd', name: 'Crystal Road EP', voice: 'Crystal Rd', bank: '1A', ratio: 2, index: 1.3, tine: 0.23, tineRatio: 8.01, decay: 3.4, cutoff: 12000, body: 0.16, chorus: 0.12 },
  { id: 'dx-dream', name: 'DX Dream EP', voice: 'DX Dream', bank: '1A', ratio: 1, index: 3.2, tine: 0.2, tineRatio: 14, decay: 2.9, cutoff: 8700, body: 0.17, chorus: 0.24, space: 0.18 },
  { id: 'dry-tines', name: 'Dry Studio Tines', voice: 'Dry Tines', bank: '1B', ratio: 1, index: 1.7, tine: 0.14, tineRatio: 7, decay: 1.5, cutoff: 6300, body: 0.35, space: 0 },
  { id: 'full-tines', name: 'Full Body Tines', voice: 'FullTines', bank: '1B', ratio: 1, index: 2.6, tine: 0.2, tineRatio: 5, decay: 2.8, cutoff: 8200, body: 0.4, chorus: 0.1 },
  { id: 'hard-tines', name: 'Hard Hammer Tines', voice: 'HardTines', bank: '1B', ratio: 1, index: 4.1, tine: 0.26, tineRatio: 11, decay: 1.3, cutoff: 11500, body: 0.23, contact: 0.022, attackDecay: 0.16 },
  { id: 'warm-rhodes', name: 'Warm Road EP', voice: 'Warm Rhdes', bank: '1B', ratio: 1, index: 0.9, tine: 0.06, tineRatio: 4, decay: 2.6, cutoff: 2900, body: 0.5, chorus: 0.08 },
  { id: 'soft-ep', name: 'Soft Touch EP', voice: 'Soft EP', bank: '1B', ratio: 1, index: 0.65, tine: 0.07, tineRatio: 5, decay: 3.2, cutoff: 4100, body: 0.42, attack: 0.014 },
  { id: 'thud-tine', name: 'Thud Tine EP', voice: 'Thud Tine', bank: '1C', ratio: 0.5, index: 1.8, tine: 0.09, tineRatio: 3, decay: 1.1, cutoff: 3400, body: 0.48, contact: 0.018 },
  { id: 'dirty-dyno', name: 'Dirty Dyno EP', voice: 'Dirty Dyno', bank: '1C', ratio: 1, index: 3.7, tine: 0.2, tineRatio: 9, decay: 1.7, cutoff: 7200, body: 0.3, dirt: 0.18 },
  { id: 'night-roads', name: 'Night Roads EP', voice: 'NiteRhodes', bank: '1C', ratio: 1, index: 1.1, tine: 0.1, tineRatio: 7, decay: 3.4, cutoff: 3600, body: 0.4, chorus: 0.18, space: 0.2 }
];

const tinePatch = (voice) => makePatch(voice.id, voice.name, 'Keys', source(voice.voice, voice.bank), {
  tags: ['Electric Piano', 'FM', voice.cutoff < 4500 ? 'Warm' : 'Bright'],
  macros: { Punch: voice.contact ? 0.38 : 0.16, Metal: 0.04, Dirt: voice.dirt || 0, Width: voice.chorus ? 0.45 : 0.12, Air: 0.06 },
  ampEnvelope: { attack: voice.attack || 0.003, decay: voice.decay, sustain: 0.06, release: voice.space ? 0.8 : 0.35 },
  filter: { cutoff: voice.cutoff },
  effects: {
    chorus: { enabled: Boolean(voice.chorus), mix: voice.chorus || 0, rate: 0.18, depth: 0.2 },
    reverb: { enabled: voice.space !== 0, mix: voice.space ?? 0.08, size: voice.space ? 0.58 : 0.3 },
    distortion: { enabled: Boolean(voice.dirt), drive: 0.22, mix: voice.dirt || 0 }
  },
  layers: [
    { enabled: true, engine: 'fm', name: 'Tine Core', waveform: 'sine', level: 0.54, ratio: voice.ratio, index: voice.index, tone: 0.44, feedback: 0 },
    { enabled: true, engine: 'fm', name: 'Hammer Bell', waveform: 'sine', level: voice.tine, ratio: voice.tineRatio, index: 1.5, tone: 0.65, feedback: 0, envelope: envelope(voice.attackDecay || 0.38, 0, 0.15) },
    { enabled: true, engine: 'analog', name: 'Round Fundamental', waveform: 'sine', level: voice.body, octave: 0, tone: 0.25, envelope: envelope(voice.decay * 1.15, 0.12, 0.4) },
    { enabled: Boolean(voice.contact), engine: 'texture', name: 'Hammer Contact', level: voice.contact || 0, tone: 0.32, density: 0.1, size: 0.08, envelope: envelope(0.025, 0, 0.015) }
  ]
});

export const SY77_INSPIRED_PRESETS = [
  ...ELECTRIC_PIANOS.map(tinePatch),
  makePatch('choir-tines', 'Choir Tines', 'Keys', source('EP+Choir', '1C'), {
    tags: ['Electric Piano', 'Choir', 'Layered'],
    macros: { Width: 0.55, Space: 0.25, Air: 0.15 },
    ampEnvelope: { decay: 3.4, sustain: 0.22, release: 1.2 },
    effects: { chorus: { enabled: true, mix: 0.22 }, reverb: { mix: 0.23, size: 0.62 } },
    layers: [
      { enabled: true, engine: 'fm', name: 'Singing Tines', ratio: 1, index: 1.7, tone: 0.42, level: 0.5 },
      { enabled: true, engine: 'additive', name: 'Choir Bloom', level: 0.24, tone: 0.38, harmonics: [1, 0.08, 0.5, 0.1, 0.3, 0.22, 0.08, 0.15], envelope: envelope(1.8, 0.55, 1.2, 0.32) },
      { enabled: true, engine: 'analog', name: 'Low Body', waveform: 'sine', level: 0.22 },
      { enabled: false }
    ]
  }),
  makePatch('soft-flute', 'Soft Air Flute', 'Wind', source('Soft Flute', '1C'), {
    tags: ['Flute', 'Acoustic'],
    ampEnvelope: { attack: 0.065, decay: 0.3, sustain: 0.78, release: 0.18 },
    filter: { cutoff: 4800 },
    lfo: { enabled: true, destination: 'pitch', rate: 4.8, depth: 0.025, sync: false },
    layers: [
      { enabled: true, engine: 'physical', name: 'Air Column', physicalModel: 'wind', level: 0.63, exciter: 0.32, breath: 0.15, bodySize: 0.55, bodyDecay: 0.55, tone: 0.4, damping: 0.62, bodyMix: 0.55, articulation: 'natural' },
      { enabled: true, engine: 'analog', name: 'Soft Fundamental', waveform: 'sine', level: 0.16 },
      { enabled: false }, { enabled: false }
    ]
  }),
  makePatch('real-brass', 'Golden Brass Ensemble', 'Brass', source('Real Brass', '1D'), {
    tags: ['Brass', 'Ensemble'],
    macros: { Punch: 0.36, Width: 0.45, Body: 0.7 },
    ampEnvelope: { attack: 0.055, decay: 0.5, sustain: 0.7, release: 0.27 },
    filter: { cutoff: 4700 },
    filterEnvelope: { attack: 0.07, decay: 0.32, sustain: 0.4, amount: 0.28 },
    layers: [
      { enabled: true, engine: 'physical', name: 'Lip & Bell', physicalModel: 'brass', level: 0.45, exciter: 0.58, bodySize: 0.62, material: 0.75, tone: 0.58, breath: 0.07, bodyMix: 0.72, damping: 0.42 },
      { enabled: true, engine: 'analog', name: 'Section Body', waveform: 'sawtooth', level: 0.22, detune: 6, unison: 2, width: 0.45 },
      { enabled: true, engine: 'fm', name: 'Brass Edge', ratio: 1, index: 1.2, level: 0.1, tone: 0.4 },
      { enabled: false }
    ]
  }),
  makePatch('nasty-lead', 'Voltage Bite Lead', 'Lead', source('Nasty Lead', '1C'), {
    tags: ['FM', 'Mono', 'Aggressive'],
    macros: { Punch: 0.6, Metal: 0.15, Dirt: 0.25, Body: 0.62 },
    ampEnvelope: { attack: 0.004, decay: 0.22, sustain: 0.74, release: 0.12 },
    filter: { cutoff: 3800, resonance: 1.6, drive: 0.1 },
    filterEnvelope: { attack: 0.003, decay: 0.25, amount: 0.36 },
    performance: { mono: true, legato: true, glide: 0.06, master: 0.56 },
    effects: { distortion: { enabled: true, drive: 0.24, mix: 0.2 }, reverb: { enabled: false } },
    layers: [
      { enabled: true, engine: 'fm', name: 'Biting Core', ratio: 2, index: 4, feedback: 0.15, tone: 0.55, level: 0.46 },
      { enabled: true, engine: 'analog', name: 'Square Body', waveform: 'square', level: 0.24 },
      { enabled: true, engine: 'analog', name: 'Sub Support', waveform: 'sine', octave: -1, level: 0.12 },
      { enabled: false }
    ]
  }),
  makePatch('geetar', 'Picked Glass Guitar', 'Pluck', source('Geetar', '1C'), {
    tags: ['Guitar', 'Plucked', 'Hybrid'],
    ampEnvelope: { attack: 0.002, decay: 1.7, sustain: 0, release: 0.22 },
    filter: { cutoff: 5700 },
    layers: [
      { enabled: true, engine: 'physical', name: 'Picked String', physicalModel: 'pluck', level: 0.65, pickPosition: 0.18, exciter: 0.5, bodySize: 0.42, bodyDecay: 0.58, material: 0.5, damping: 0.57, tone: 0.46, bodyMix: 0.7, mechanics: 0.18 },
      { enabled: true, engine: 'fm', name: 'Glass Pick', ratio: 3, index: 1.2, tone: 0.5, level: 0.12, envelope: envelope(0.12, 0, 0.06) },
      { enabled: false }, { enabled: false }
    ]
  })
];
